import { HttpErrorResponse } from '@angular/common/http';

import { Selector } from '@ngrx/store/src/models';

import { ConfigObject, MakeRef, MetaState, PaginationState, Reference } from '../types';

export type ID = string | number;
export const DEFAULT_SCOPE_NAME = 'main';
export type Scope = typeof DEFAULT_SCOPE_NAME | string;
export type IdSelector<T> = (e: T) => ID;
export type OptimisticUpdate<T extends BaseEntity> = { update: EntityPartial<T>; before: T };
export interface EntityState<T extends BaseEntity> {
  entityMap: Record<ID, T | null>;
  entityMetaMap: Record<ID, MetaState>;
  scope: Record<Scope, ID[]>;
  scopeMeta: Record<Scope, MetaState>;
  aliases: Record<ID, ID>;
  optimisticUpdate: Record<string, OptimisticUpdate<T>>;
  optimisticDelete: Record<ID, T>;
  scopePagination: Record<Scope, PaginationState>;
}

export interface CorrelationId {
  correlationId?: string;
}

export interface EntityAdapterOptions {
  scope?: string;
}

export interface BaseEntity {
  [key: string]: any;
}

export type EntityPartial<T extends BaseEntity> = Partial<T>;

export type BaseSelector<T extends BaseEntity> = Selector<any, EntityState<T>>;

export type MetaScopeOptions = { scope: Scope | null };
export type MetaEntitiesOptions = { entities: ID[] };
export type MetaActionOptions = MetaScopeOptions & MetaEntitiesOptions;

export interface EntityMetaAdapter<T extends BaseEntity> {
  markAsPending: <T1 extends BaseEntity>(
    state: EntityState<T1>,
    options?: MetaActionOptions,
  ) => EntityState<T1>;
  markAsLoaded: <T1 extends BaseEntity>(
    state: EntityState<T1>,
    options?: MetaActionOptions,
  ) => EntityState<T1>;
  setError: (
    state: EntityState<T>,
    error: HttpErrorResponse,
    options?: MetaActionOptions,
  ) => EntityState<T>;
  setPaginationMeta: (
    state: EntityState<T>,
    paginationMeta: PaginationState,
    options?: MetaScopeOptions,
  ) => EntityState<T>;
  // alias correlationId -> id for remote create operations
  setAlias: (state: EntityState<T>, data: Record<ID, ID>) => EntityState<T>;
}

export interface EntityDataAdapter<T extends BaseEntity> {
  // Add to scope (uniq), set entity
  addOne: (
    state: EntityState<T>,
    data: T,
    options?: EntityAdapterOptions & CorrelationId,
  ) => EntityState<T>;
  addMany: (state: EntityState<T>, data: T[], options?: EntityAdapterOptions) => EntityState<T>;

  // Set scope, set entity
  setOne: (state: EntityState<T>, data: T, options: EntityAdapterOptions) => EntityState<T>;
  setMany: (state: EntityState<T>, data: T[], options: EntityAdapterOptions) => EntityState<T>;

  // Doesn't touch scope, set entity
  writeOne: (state: EntityState<T>, data: T) => EntityState<T>;
  writeMany: (state: EntityState<T>, data: T[]) => EntityState<T>;

  // Replace entity in all scopes, set entity
  replaceOne: (state: EntityState<T>, previousId: ID, current: T) => EntityState<T>;
  replaceMany: (state: EntityState<T>, data: { previousId: ID; current: T }[]) => EntityState<T>;

  // Removes entity from all scopes, removes entity
  removeOne: (state: EntityState<T>, id: ID) => EntityState<T>;
  removeMany: (state: EntityState<T>, ids: ID[]) => EntityState<T>;

  // Doesn't touch scope, updates entity (spread)
  updateOne: (state: EntityState<T>, data: EntityPartial<T>) => EntityState<T>;
  updateMany: (state: EntityState<T>, data: EntityPartial<T>[]) => EntityState<T>;

  // Add to scope if not exist, update or add entity
  upsertOne: (state: EntityState<T>, data: T, options?: EntityAdapterOptions) => EntityState<T>;
  upsertMany: (state: EntityState<T>, data: T[], options?: EntityAdapterOptions) => EntityState<T>;

  optimisticUpdateSet: (
    state: EntityState<T>,
    correlationId: ID,
    data: OptimisticUpdate<T>,
  ) => EntityState<T>;
  optimisticUpdateRemove: (state: EntityState<T>, correlationId: string) => EntityState<T>;
  optimisticDeleteSet: (state: EntityState<T>, data: T) => EntityState<T>;
  optimisticDeleteRemove: (state: EntityState<T>, id: ReturnType<IdSelector<T>>) => EntityState<T>;

  // Reset to initial state
  resetAll: (state: EntityState<T>) => EntityState<T>;
}

export type EntityCollectionAdapter<T extends BaseEntity> = EntityMetaAdapter<T> &
  EntityDataAdapter<T>;

export type EntityCollectionCustomActionsCreator<T> = (
  adapter: EntityCollectionAdapter<T>,
) => ConfigObject<EntityState<T>>;

export interface EntityCollectionStoreOptions<T> {
  idSelector?: IdSelector<T>;
  customActions?: EntityCollectionCustomActionsCreator<T>;
  references?: (makeRef: MakeRef<T>) => Reference<T>[];
  initialState?: EntityState<T>;
}

export type EntityCollectionStoreCustomActions<T extends EntityCollectionStoreOptions<any>> =
  T['customActions'] extends EntityCollectionCustomActionsCreator<any>
    ? ReturnType<T['customActions']>
    : {};
